import { type KeyboardEvent, type SyntheticEvent, useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { InputGroup, InputGroupSuffix } from '@components/ui/input-group'
import { Slider } from '@components/ui/slider'
import type { BackgroundOptionsItem } from '@/lib/backgrounds/background-options-layout'
import type { BackgroundDropSlot } from '@/lib/backgrounds/types/background-drop-slot'
import { clampBackgroundTint } from '@/lib/backgrounds/saved-spread-backgrounds'

// The toolbar lives over the pasteboard, whose own pointer handlers start marquee selection,
// deselect, pan, etc. — none of which should fire for a click on a toolbar control.
const stopCanvasPropagation = (event: SyntheticEvent) => event.stopPropagation()

type BackgroundOptionsToolbarProps = {
  item: BackgroundOptionsItem
  tint: number
  onTintChange: (slot: BackgroundDropSlot, tint: number, persist: boolean) => void
  onRemove: (slot: BackgroundDropSlot) => void
}

export function BackgroundOptionsToolbar({
  item,
  tint,
  onTintChange,
  onRemove,
}: BackgroundOptionsToolbarProps) {
  const { slot, label } = item
  const [draft, setDraft] = useState(String(tint))
  const [isEditing, setIsEditing] = useState(false)

  // Keep the field in sync with slider drags, undo-less server settles, etc. — but never
  // overwrite what the user is in the middle of typing.
  useEffect(() => {
    if (!isEditing) setDraft(String(tint))
  }, [isEditing, tint])

  const commitDraft = () => {
    setIsEditing(false)

    const parsed = Number(draft)

    if (draft.trim() === '' || !Number.isFinite(parsed)) {
      setDraft(String(tint))
      onTintChange(slot, tint, true)
      return
    }

    const next = clampBackgroundTint(parsed)

    setDraft(String(next))
    onTintChange(slot, next, true)
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.currentTarget.blur()
    }
  }

  const inputId = `background-tint-${slot}`

  return (
    <div
      role="toolbar"
      aria-label={`${label.replace(/ tint$/, '')} background options`}
      data-background-options={slot}
      className="pointer-events-auto flex shrink-0 items-center gap-4 whitespace-nowrap rounded-lg border border-border bg-background px-3 py-2 shadow-md"
      onPointerDown={stopCanvasPropagation}
      onMouseDown={stopCanvasPropagation}
      onClick={stopCanvasPropagation}
      onDoubleClick={stopCanvasPropagation}
      onContextMenu={stopCanvasPropagation}
    >
      <label htmlFor={inputId} className="text-sm font-semibold text-foreground">
        {label}
      </label>
      <Slider
        className="w-[150px]"
        value={[tint]}
        min={0}
        max={100}
        step={1}
        thumbLabel={label}
        onValueChange={([next]) => onTintChange(slot, next ?? tint, false)}
        onValueCommit={([next]) => onTintChange(slot, next ?? tint, true)}
      />
      <InputGroup className="w-[80px]">
        <Input
          id={inputId}
          size="sm"
          inputMode="numeric"
          className="pr-7 text-sm"
          value={draft}
          onFocus={() => setIsEditing(true)}
          onChange={(event) => {
            const value = event.target.value.replace(/[^\d]/g, '').slice(0, 3)

            setDraft(value)
            if (value !== '') onTintChange(slot, clampBackgroundTint(Number(value)), false)
          }}
          onBlur={commitDraft}
          onKeyDown={handleInputKeyDown}
        />
        <InputGroupSuffix className="text-sm text-muted-foreground">%</InputGroupSuffix>
      </InputGroup>
      <Button type="button" variant="secondary" size="sm" onClick={() => onRemove(slot)}>
        <Trash2 aria-hidden="true" />
        Remove background
      </Button>
    </div>
  )
}
